import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeDollarSign, Paperclip, Check, X, Search, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 ring-amber-300",
  accepted: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  dismissed: "bg-slate-100 text-slate-600 ring-slate-200",
};

/** Finance inbox: every client-reported payment, with filters and bulk accept. */
export default function PaymentClaims() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [clientId, setClientId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [dismissing, setDismissing] = useState(null);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(false);

  const params = useMemo(() => ({
    ...(status !== "all" ? { status } : {}),
    ...(clientId !== "all" ? { client_id: clientId } : {}),
    ...(from ? { date_from: from } : {}),
    ...(to ? { date_to: to } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  }), [status, clientId, from, to, q]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["payment-claims", "inbox", params],
    queryFn: () => api.listPaymentClaims(params),
  });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.listClients() });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["payment-claims"] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    setSelected(new Set());
  };
  const onErr = (e) => toast.error(e?.response?.data?.detail || "Request failed");
  const acceptMut = useMutation({
    mutationFn: (id) => api.acceptPaymentClaim(id),
    onSuccess: (res) => {
      toast.success(res.recorded.length ? `Payment recorded on ${res.recorded.length} invoice(s)` : "Nothing left to record");
      if (res.client_notified) toast.info(`Thank-you email sent to ${res.client_notified}`);
      refresh();
    },
    onError: onErr,
  });
  const bulkMut = useMutation({
    mutationFn: (ids) => api.bulkAcceptPaymentClaims(ids),
    onSuccess: (res) => {
      toast.success(`${res.accepted_count} claim${res.accepted_count === 1 ? "" : "s"} accepted`);
      if (res.error_count) toast.error(`${res.error_count} could not be accepted: ${res.errors.map((e) => e.detail).join("; ")}`);
      setBulkOpen(false); refresh();
    },
    onError: onErr,
  });
  const dismissMut = useMutation({
    mutationFn: ({ id, reason: r, notify: n }) => api.dismissPaymentClaim(id, { reason: r, notify_client: n }),
    onSuccess: (res) => {
      toast.success(res.client_notified ? `Dismissed — client notified at ${res.client_notified}` : "Report dismissed");
      setDismissing(null); setReason(""); setNotify(false); refresh();
    },
    onError: onErr,
  });

  const pendingRows = rows.filter((r) => r.status === "pending");
  const selectedRows = pendingRows.filter((r) => selected.has(r.id));
  const selectedTotals = selectedRows.reduce((acc, r) => {
    acc[r.currency] = (acc[r.currency] || 0) + r.amount; return acc;
  }, {});
  const allSelected = pendingRows.length > 0 && selectedRows.length === pendingRows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pendingRows.map((r) => r.id)));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div data-testid="payment-claims-page">
      <PageHeader
        overline="Finance"
        title="Payment Claims"
        subtitle="Every payment clients reported through their pay link. Accept to record it, or dismiss with a note."
        actions={
          <Button data-testid="claims-bulk-accept-btn" disabled={!selectedRows.length} onClick={() => setBulkOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCheck className="size-4 mr-1.5" /> Accept selected ({selectedRows.length})
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="claims-filters">
        <div>
          <Label className="overline">Status</Label>
          <Select value={status} onValueChange={(v) => { setStatus(v); setSelected(new Set()); }}>
            <SelectTrigger data-testid="claims-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Client</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="claims-filter-client"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Reported from</Label>
          <DatePicker testId="claims-filter-from" value={from} onChange={setFrom} />
        </div>
        <div>
          <Label className="overline">Reported to</Label>
          <DatePicker testId="claims-filter-to" value={to} onChange={setTo} />
        </div>
        <div>
          <Label className="overline">Search</Label>
          <div className="relative">
            <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="claims-filter-q" className="pl-8" placeholder="Reference, invoice #, client…"
                   value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div data-testid="claims-empty"><EmptyState title="No payment claims" hint="Nothing matches these filters." /></div>
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8">
                  <Checkbox data-testid="claims-select-all" checked={allSelected} disabled={!pendingRows.length}
                            onCheckedChange={toggleAll} aria-label="Select all pending" />
                </TableHead>
                {["Reported", "Client", "Invoices", "Amount", "Paid on · method", "Reference", "Status", ""].map((h, i) => (
                  <TableHead key={i} className="overline">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`claim-row-${c.id}`} className={selected.has(c.id) ? "bg-emerald-50/50" : ""}>
                  <TableCell>
                    {c.status === "pending" && (
                      <Checkbox data-testid={`claim-select-${c.id}`} checked={selected.has(c.id)}
                                onCheckedChange={() => toggle(c.id)} aria-label="Select claim" />
                    )}
                  </TableCell>
                  <TableCell className="mono text-xs whitespace-nowrap">
                    {fmtDate(c.created_at.slice(0, 10))}
                    {c.reporter_name && <div className="text-[10px] text-muted-foreground">{c.reporter_name}</div>}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    <Link to={`/clients/${c.client_id}`} className="text-primary hover:underline">{c.client_name}</Link>
                  </TableCell>
                  <TableCell className="text-xs">
                    {c.allocations.map((a) => (
                      <div key={a.invoice_id}>
                        <Link to={`/invoices/${a.invoice_id}`} className="mono hover:underline">{a.invoice_number}</Link>
                        <span className="text-muted-foreground"> · {fmtMoney(a.amount)}</span>
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="mono text-sm font-semibold whitespace-nowrap">{fmtMoney(c.amount)} {c.currency}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDate(c.paid_on)} · {c.method.replace("_", " ")}</TableCell>
                  <TableCell className="mono text-xs">{c.reference || "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-1.5 py-0.5 rounded-md text-[10px] font-semibold ring-1 ring-inset capitalize ${STATUS_STYLE[c.status]}`}
                          data-testid={`claim-status-${c.id}`}>
                      {c.status}
                    </span>
                    {c.status !== "pending" && c.reviewed_by && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{c.reviewed_by}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {c.proof && (
                      <Button variant="ghost" size="sm" data-testid={`claim-proof-${c.id}`} onClick={() => api.openPaymentClaimProof(c.id)}>
                        <Paperclip className="size-3.5" />
                      </Button>
                    )}
                    {c.status === "pending" && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-1" data-testid={`claim-accept-${c.id}`}
                                disabled={acceptMut.isPending} onClick={() => acceptMut.mutate(c.id)}>
                          <Check className="size-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 ml-1" data-testid={`claim-dismiss-${c.id}`}
                                onClick={() => setDismissing(c)}>
                          <X className="size-3.5" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="claims-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Accept {selectedRows.length} payment claim{selectedRows.length === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>
              This records real payments totalling{" "}
              <b className="mono" data-testid="claims-bulk-total">
                {Object.entries(selectedTotals).map(([c, v]) => `${fmtMoney(v)} ${c}`).join(" · ")}
              </b>{" "}
              across {selectedRows.reduce((n, r) => n + r.allocations.length, 0)} invoice(s), sends receipts and thank-you emails.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="claims-bulk-confirm"
                    disabled={bulkMut.isPending} onClick={() => bulkMut.mutate(selectedRows.map((r) => r.id))}>
              {bulkMut.isPending ? "Recording…" : "Accept & record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dismissing} onOpenChange={(v) => !v && setDismissing(null)}>
        <DialogContent data-testid="claim-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Dismiss this payment report?</DialogTitle>
            <DialogDescription>No payment will be recorded.</DialogDescription>
          </DialogHeader>
          <Input data-testid="claim-dismiss-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="claim-dismiss-notify" checked={notify} onCheckedChange={(v) => setNotify(!!v)} />
            Notify client by email
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissing(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="claim-dismiss-confirm" disabled={dismissMut.isPending}
                    onClick={() => dismissMut.mutate({ id: dismissing.id, reason, notify })}>Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
